import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Title,
  Text,
  Group,
  Stack,
  Badge,
  Button,
  Divider,
  Loader,
  Center,
  Select,
  Textarea,
  TextInput,
  Breadcrumbs,
  Anchor,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { lostClaimsApi } from '../api/lostClaims';
import { itemsApi } from '../api/items';
import { categoriesApi } from '../api/categories';
import { profileLink } from '../utils/profileLink';
import { buildCategorySelectData } from '../utils/categories';
import { CLAIM_STATUS_LABELS, CLAIM_STATUS_COLORS } from '../constants/status';
import { ImageUpload } from '../components/ImageUpload';
import type { ClaimStatus, CreateClaimInput } from '../types';
import { pageContainer } from '../styles/theme.css';
import { getSemester } from '../utils/semester';


const CONTACT_METHODS = [
  { label: 'Telefon', value: 'phone' },
  { label: 'Email', value: 'email' },
  { label: 'Instagram', value: 'instagram' },
  { label: 'Facebook', value: 'facebook' },
  { label: 'Andet', value: 'other' },
];

export function LostClaimDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const claimId = Number(id);
  const [editing, setEditing] = useState(false);

  const { data: claim, isLoading, error } = useQuery({
    queryKey: ['lost-claims', claimId],
    queryFn: () => lostClaimsApi.get(claimId),
    enabled: !isNaN(claimId),
  });

  const { data: catData } = useQuery({
    queryKey: ['categories'],
    queryFn: categoriesApi.get,
    staleTime: Infinity,
  });

  const categorySelectData = buildCategorySelectData(catData);

  const form = useForm<Partial<CreateClaimInput>>({ initialValues: {} });

  function startEditing() {
    if (!claim) return;
    form.setValues({
      category_id: claim.category_id ?? null,
      description: claim.description,
      color: claim.color ?? '',
      brand: claim.brand ?? '',
      distinctive_features: claim.distinctive_features ?? '',
      lost_date: claim.lost_date ?? '',
      contact_name: claim.contact_name,
      contact_method: claim.contact_method,
      social_profile: claim.social_profile ?? '',
    });
    setEditing(true);
  }

  const updateMutation = useMutation({
    mutationFn: (data: Partial<CreateClaimInput> & { status?: ClaimStatus }) =>
      lostClaimsApi.update(claimId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lost-claims'] });
      notifications.show({ message: 'Efterlysning opdateret', color: 'green' });
      setEditing(false);
    },
    onError: (err) => {
      notifications.show({ message: (err as Error).message, color: 'red' });
    },
  });

  const unmatchMutation = useMutation({
    mutationFn: () => lostClaimsApi.unmatch(claimId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lost-claims'] });
      qc.invalidateQueries({ queryKey: ['suggestions'] });
      notifications.show({ message: 'Match fortrudt', color: 'blue' });
    },
    onError: (err) => {
      notifications.show({ message: (err as Error).message, color: 'red' });
    },
  });

  const markDeliveredMutation = useMutation({
    mutationFn: () =>
      Promise.all([
        lostClaimsApi.update(claimId, { status: 'closed' }),
        ...(claim?.matched_item_id ? [itemsApi.update(claim.matched_item_id, { status: 'returned' })] : []),
      ]),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lost-claims'] });
      qc.invalidateQueries({ queryKey: ['items'] });
      notifications.show({ message: 'Genstand markeret som udleveret', color: 'green' });
    },
    onError: (err) => {
      notifications.show({ message: (err as Error).message, color: 'red' });
    },
  });

  const addImageMutation = useMutation({
    mutationFn: ({ url, ziplineId }: { url: string; ziplineId?: string }) =>
      lostClaimsApi.addImage(claimId, url, ziplineId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lost-claims', claimId] }),
  });

  const deleteImageMutation = useMutation({
    mutationFn: (imageId: number) => lostClaimsApi.deleteImage(claimId, imageId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lost-claims', claimId] }),
  });

  if (isLoading)
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  if (error || !claim)
    return (
      <Text c="red" p="md">
        Efterlysning ikke fundet.
      </Text>
    );

  // Map images to the shape ImageUpload expects
  const uploadImages = claim.images.map((img) => ({
    id: img.id,
    item_id: img.claim_id,
    zipline_url: img.zipline_url,
    created_at: img.created_at,
  }));

  return (
    <div className={pageContainer}>
      <Stack gap="md">
        <Breadcrumbs>
          <Anchor onClick={() => navigate('/lost-claims')} size="sm">
            Efterlysninger
          </Anchor>
          <Text size="sm" lineClamp={1}>
            {claim.description}
          </Text>
        </Breadcrumbs>

        <Group justify="space-between" align="flex-start">
          <Stack gap={4}>
            <Title order={2}>Efterlysning #{claim.id}</Title>
            <Badge color={CLAIM_STATUS_COLORS[claim.status]} variant="light">
              {CLAIM_STATUS_LABELS[claim.status]}
            </Badge>
          </Stack>
          <Group gap="xs">
            {!editing && (
              <Button variant="default" size="xs" onClick={startEditing}>
                Rediger
              </Button>
            )}
          </Group>
        </Group>

        {/* Status actions */}
        {!editing && (
          <Group gap="xs">
            {claim.status === 'matched' && (
              <>
                <Button
                  size="xs"
                  color="green"
                  onClick={() => markDeliveredMutation.mutate()}
                  loading={markDeliveredMutation.isPending}
                >
                  Marker som udleveret
                </Button>
                <Button
                  size="xs"
                  color="gray"
                  variant="subtle"
                  onClick={() => unmatchMutation.mutate()}
                  loading={unmatchMutation.isPending}
                >
                  Fortryd match
                </Button>
              </>
            )}
            {claim.status === 'open' && (
              <Button
                size="xs"
                color="gray"
                variant="light"
                onClick={() => updateMutation.mutate({ status: 'closed' })}
                loading={updateMutation.isPending}
              >
                Luk efterlysning
              </Button>
            )}
            {claim.status === 'closed' && (
              <Button
                size="xs"
                color="orange"
                variant="light"
                onClick={() => updateMutation.mutate({ status: 'open' })}
                loading={updateMutation.isPending}
              >
                Genåbn efterlysning
              </Button>
            )}
          </Group>
        )}

        {claim.matchedItem && (
          <Group gap="xs">
            <Text size="sm">Matchet genstand:</Text>
            <Anchor size="sm" onClick={() => navigate(`/items/${claim.matchedItem!.id}`)}>
              {claim.matchedItem.title}
            </Anchor>
          </Group>
        )}

        <Divider />

        {editing ? (
          <form onSubmit={form.onSubmit((v) => updateMutation.mutate(v))}>
            <Stack gap="sm">
              <TextInput label="Beskrivelse" placeholder="f.eks. Sort læderjakke" required {...form.getInputProps('description')} />
              <Select
                label="Kategori"
                placeholder="Vælg kategori (valgfrit)"
                data={categorySelectData}
                searchable
                clearable
                {...form.getInputProps('category_id')}
              />
              <Group grow>
                <TextInput label="Farve" {...form.getInputProps('color')} />
                <TextInput label="Mærke" {...form.getInputProps('brand')} />
              </Group>
              <Textarea
                label="Særlige kendetegn"
                {...form.getInputProps('distinctive_features')}
              />
              <Stack gap={4}>
                <TextInput label="Mistet dato" type="date" {...form.getInputProps('lost_date')} />
                {form.values.lost_date && (
                  <Badge size="sm" variant="light" color="blue" w="fit-content">
                    {getSemester(form.values.lost_date)}
                  </Badge>
                )}
              </Stack>
              <TextInput label="Navn" required {...form.getInputProps('contact_name')} />
              <Select
                label="Kontaktmetode"
                required
                data={CONTACT_METHODS}
                {...form.getInputProps('contact_method')}
              />
              <TextInput
                label="Socialt medie / kontaktoplysning"
                {...form.getInputProps('social_profile')}
              />
              <Group>
                <Button type="submit" loading={updateMutation.isPending}>
                  Gem
                </Button>
                <Button variant="default" onClick={() => setEditing(false)}>
                  Annuller
                </Button>
              </Group>
            </Stack>
          </form>
        ) : (
          <Stack gap="xs">
            <Text size="sm">
              <b>Beskrivelse:</b> {claim.description}
            </Text>
            {claim.category_id && (
              <Text size="sm">
                <b>Kategori:</b>{' '}
                {catData?.categories.find((c) => c.id === claim.category_id)?.label_da ?? claim.category_id}
              </Text>
            )}
            {claim.color && (
              <Text size="sm">
                <b>Farve:</b> {claim.color}
              </Text>
            )}
            {claim.brand && (
              <Text size="sm">
                <b>Mærke:</b> {claim.brand}
              </Text>
            )}
            {claim.distinctive_features && (
              <Text size="sm">
                <b>Særlige kendetegn:</b> {claim.distinctive_features}
              </Text>
            )}
            {claim.lost_date && (
              <Text size="sm">
                <b>Mistet:</b>{' '}
                {new Date(claim.lost_date + 'T12:00:00').toLocaleDateString('da-DK')}{' '}
                <Badge size="xs" variant="light" color="blue" ml={4}>
                  {getSemester(claim.lost_date)}
                </Badge>
              </Text>
            )}
            <Text size="sm">
              <b>Kontakt:</b> {claim.contact_name}
            </Text>
            <Text size="sm">
              <b>Kontaktmetode:</b>{' '}
              {CONTACT_METHODS.find((m) => m.value === claim.contact_method)?.label ??
                claim.contact_method}
            </Text>
            {claim.social_profile && (
              <Text size="sm">
                <b>Profil:</b>{' '}
                {(() => {
                  const href = profileLink(claim.contact_method, claim.social_profile);
                  return href ? (
                    <Anchor href={href} target="_blank" rel="noopener noreferrer" size="sm">
                      {claim.social_profile}
                    </Anchor>
                  ) : (
                    claim.social_profile
                  );
                })()}
              </Text>
            )}
            <Text size="xs" c="dimmed">
              Registreret {new Date(claim.created_at).toLocaleDateString('da-DK')}
            </Text>
          </Stack>
        )}

        <Divider label="Billeder" />

        <ImageUpload
          existingImages={uploadImages}
          onUpload={(url, ziplineId) => addImageMutation.mutate({ url, ziplineId })}
          onDelete={(imageId) => deleteImageMutation.mutate(imageId)}
        />
      </Stack>
    </div>
  );
}
